import { DocumentDuplicateIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { cn } from '../../lib/utils';
import React from 'react';
import toast from 'react-hot-toast';
import { CARD, FIELD_LABEL } from './detail-classes';

export const SectionCard: React.FC<{
  title: string;
  /** Shown on the right of the heading. */
  aside?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}> = ({ title, aside, children, className }) => (
  <section className={clsx(CARD, className)}>
    <div className="mb-4 mt-2 flex items-baseline justify-between gap-3">
      <h2 className="text-md font-bold uppercase tracking-wider text-gray-700">
        {title}
      </h2>
      {aside && <span className="text-xs text-gray-500">{aside}</span>}
    </div>
    {children}
  </section>
);

export const BreakdownRow: React.FC<{
  label: React.ReactNode;
  value: string;
  valueClassName?: string;
  bold?: boolean;
}> = ({ label, value, valueClassName, bold }) => (
  <div
    className={clsx(
      'flex items-center justify-between gap-3 py-2 text-sm',
      bold ? 'font-bold text-gray-900' : 'text-gray-700'
    )}
  >
    <span>{label}</span>
    <span className={valueClassName}>{value}</span>
  </div>
);

export const CopyButton: React.FC<{ value: string; label: string }> = ({
  value,
  label
}) => {
  const handleCopy = () => {
    navigator.clipboard.writeText(value);
    toast.success('Copied!', { duration: 1500 });
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      aria-label={`Copy ${label}`}
      className="ml-2 rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
    >
      <DocumentDuplicateIcon className="h-5 w-5" />
    </button>
  );
};

export const DetailField: React.FC<{
  label: string;
  value: React.ReactNode;
  copyValue?: string;
  className?: string;
  valueClassName?: string;
}> = ({ label, value, copyValue, className, valueClassName }) => (
  <div className={clsx('py-2', className)}>
    <p className={clsx('mb-1', FIELD_LABEL)}>{label}</p>
    <div className="flex items-center justify-between">
      <p className={cn('text-sm font-medium text-gray-900', valueClassName)}>
        {value}
      </p>
      {copyValue && <CopyButton value={copyValue} label={label} />}
    </div>
  </div>
);

const STATUS_PILLS: Record<string, { text: string; className: string }> = {
  success: { text: 'Success', className: 'text-emerald-600 bg-emerald-100' },
  confirmed: { text: 'Success', className: 'text-green-600 bg-green-100' },
  failed: { text: 'Failed', className: 'text-red-600 bg-red-100' },
  pending: { text: 'Pending', className: 'text-gray-500 bg-gray-100' },
  paid: { text: 'Paid', className: 'text-emerald-700 bg-emerald-100' },
  processing: {
    text: 'Processing',
    className: 'text-orange-700 bg-orange-100'
  },
  /** A donation whose charge did not go through: nothing was taken, so it is not an error. */
  not_charged: { text: 'Not charged', className: 'text-gray-600 bg-gray-100' }
};

export const StatusPill: React.FC<{ status: string; className?: string }> = ({
  status,
  className
}) => {
  const config = STATUS_PILLS[status] ?? {
    text: status,
    className: 'text-gray-500'
  };

  return (
    <span
      className={clsx(
        'rounded p-1 text-sm font-semibold uppercase tracking-[0.1em]',
        config.className,
        className
      )}
    >
      {config.text}
    </span>
  );
};
